import { useRef, useState } from "react";

/**
 * Swipeable card wrapper. Tap → onOpen. Swipe past threshold → action.
 *
 * Props:
 *  - onOpen()                                 — fired on a plain tap (no drag)
 *  - right: { label, icon: Icon, tone, onTrigger() }  — optional right-swipe action
 *  - left:  { label, icon: Icon, tone, onTrigger() }  — optional left-swipe action
 *  - testid
 *
 * Tone keys: "emerald" | "amber" | "blue" | "rose" | "slate"
 */
const TONES = {
  emerald: { soft: "bg-[#002060] text-[#FFFFFF]", solid: "bg-[#002060] text-[#FFFFFF]" },
  amber: { soft: "bg-[#FF6600] text-[#FF6600]", solid: "bg-[#FF6600] text-[#FFFFFF]" },
  blue: { soft: "bg-[#FF6600]/10 text-[#FF6600]", solid: "bg-[#FF6600] text-[#FFFFFF]" },
  rose: { soft: "bg-[#FF6600]/10 text-[#FF6600]", solid: "bg-[#FF6600]/100 text-[#FFFFFF]" },
  slate: { soft: "bg-[#002060] text-[#FFFFFF]", solid: "bg-[#002060] text-[#FFFFFF]" },
};

const TRIGGER = 96;
const MAX_REVEAL = 160;

export default function SwipeCard({ onOpen, right, left, testid, children }) {
  const startRef = useRef(null);
  const movedRef = useRef(false);
  const armedRef = useRef(false);
  const [dx, setDx] = useState(0);

  const direction = dx > 0 ? "right" : dx < 0 ? "left" : null;
  const action = direction === "right" ? right : direction === "left" ? left : null;
  const armed = action && Math.abs(dx) >= TRIGGER;

  // Haptic buzz on threshold crossing (mobile-only; navigator.vibrate is a no-op on desktop)
  if (armed !== armedRef.current) {
    armedRef.current = armed;
    if (armed && typeof navigator !== "undefined" && navigator.vibrate) {
      try { navigator.vibrate(15); } catch { /* ignore */ }
    }
  }

  function onPointerDown(e) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    startRef.current = { x: e.clientX, y: e.clientY };
    movedRef.current = false;
  }
  function onPointerMove(e) {
    if (!startRef.current) return;
    const ddx = e.clientX - startRef.current.x;
    const ddy = e.clientY - startRef.current.y;
    if (Math.abs(ddy) > 14 && Math.abs(ddy) > Math.abs(ddx)) {
      startRef.current = null;
      setDx(0);
      return;
    }
    if (Math.abs(ddx) > 6) movedRef.current = true;
    let clamped = ddx;
    if (ddx > 0 && !right) clamped = 0;
    if (ddx < 0 && !left) clamped = 0;
    clamped = Math.max(-MAX_REVEAL, Math.min(MAX_REVEAL, clamped));
    setDx(clamped);
  }
  function onPointerUp(e) {
    if (!startRef.current) return;
    const ddx = e.clientX - startRef.current.x;
    const moved = movedRef.current;
    startRef.current = null;
    setDx(0);
    armedRef.current = false;
    const fired = moved && ((right && ddx >= TRIGGER) || (left && ddx <= -TRIGGER));
    if (fired && typeof navigator !== "undefined" && navigator.vibrate) {
      try { navigator.vibrate(25); } catch { /* ignore */ }
    }
    if (moved && right && ddx >= TRIGGER) right.onTrigger();
    else if (moved && left && ddx <= -TRIGGER) left.onTrigger();
    else if (!moved) onOpen && onOpen();
  }
  function onPointerCancel() {
    startRef.current = null;
    setDx(0);
  }

  const noActions = !right && !left;
  if (noActions) {
    return (
      <button type="button" onClick={onOpen} data-testid={testid} className="w-full text-left">
        {children}
      </button>
    );
  }

  return (
    <div className="relative" data-testid={testid}>
      {/* Right reveal */}
      {right && (
        <RevealPanel
          dx={Math.max(0, dx)}
          side="left"
          armed={armed && direction === "right"}
          tone={right.tone || "emerald"}
          icon={right.icon}
          label={right.label}
        />
      )}
      {/* Left reveal */}
      {left && (
        <RevealPanel
          dx={Math.max(0, -dx)}
          side="right"
          armed={armed && direction === "left"}
          tone={left.tone || "amber"}
          icon={left.icon}
          label={left.label}
        />
      )}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        style={{
          transform: `translateX(${dx}px)`,
          transition: dx === 0 ? "transform 180ms ease-out" : "none",
          touchAction: "pan-y",
        }}
        className="relative w-full text-left bg-[#002060] border border-[#FFFFFF]/15 rounded-lg cursor-pointer select-none"
      >
        {children}
      </div>
    </div>
  );
}

function RevealPanel({ dx, side, armed, tone, icon: Icon, label }) {
  const t = TONES[tone] || TONES.slate;
  const sideClass = side === "left"
    ? "left-0 rounded-l-lg justify-start pl-4"
    : "right-0 rounded-r-lg justify-end pr-4";
  return (
    <div
      className={`absolute inset-y-0 ${sideClass} flex items-center transition-colors ${
        armed ? t.solid : t.soft
      }`}
      style={{ width: dx }}
      aria-hidden="true"
    >
      {dx > 30 && (
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest whitespace-nowrap">
          {Icon && <Icon className="w-3.5 h-3.5" />}
          {armed ? `Release · ${label}` : label}
        </span>
      )}
    </div>
  );
}
